/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useState, useCallback, useRef } from 'react';
import type { FlowStream, VPCFlowRecord } from './types';
import { WELL_KNOWN_PORTS } from './types';
import { getProtocolName, isIPInCIDR, isPrivateIP } from './parser';

export interface ParseProgress {
  progress: number;
  phase: 'idle' | 'reading' | 'parsing' | 'aggregating' | 'complete' | 'error';
  currentLine?: number;
  totalLines?: number;
}

export interface ParseStats {
  totalRecords: number;
  validRecords: number;
  skippedRecords: number;
  uniqueStreams: number;
  acceptCount: number;
  rejectCount: number;
}

export interface UseFlowParserReturn {
  parseFlowLogs: (text: string, vpcCidrs?: string[]) => void;
  cancelParsing: () => void;
  streams: FlowStream[];
  progress: ParseProgress;
  stats: ParseStats | null;
  error: string | null;
  isProcessing: boolean;
}

// Parse a single flow log line
// Supports two formats:
// 1. With timestamp: "2026-01-09T22:00:16+00:00 eni-xxx-all 2 account-id eni-xxx srcaddr dstaddr srcport dstport protocol packets bytes start end action log-status"
// 2. JSON message format: "2 account-id eni-xxx srcaddr dstaddr srcport dstport protocol packets bytes start end action log-status"
function parseLine(line: string): VPCFlowRecord | null {
  const parts = line.trim().split(/\s+/);
  
  // Detect format based on first field
  let offset = 0;
  let timestamp = '';
  
  // Check if first field looks like a timestamp (contains 'T' or '-' for ISO format)
  if (parts[0] && (parts[0].includes('T') || parts[0].match(/^\d{4}-\d{2}-\d{2}/))) {
    // Format 1: Has timestamp prefix
    offset = 2; // Skip timestamp and eni-xxx-all
    timestamp = parts[0];
    if (parts.length < 16) return null;
  } else if (parts[0] && /^\d+$/.test(parts[0])) {
    // Format 2: JSON message format (starts with version number)
    offset = 0;
    timestamp = new Date().toISOString();
    if (parts.length < 14) return null;
  } else {
    return null;
  }
  
  // Calculate field indices based on offset
  const versionIdx = offset;
  const accountIdx = offset + 1;
  const eniIdx = offset + 2;
  const srcAddrIdx = offset + 3;
  const dstAddrIdx = offset + 4;
  const srcPortIdx = offset + 5;
  const dstPortIdx = offset + 6;
  const protocolIdx = offset + 7;
  const packetsIdx = offset + 8;
  const bytesIdx = offset + 9;
  const startIdx = offset + 10;
  const endIdx = offset + 11;
  const actionIdx = offset + 12;
  const statusIdx = offset + 13;
  
  // Skip if no data (addresses are '-')
  if (parts[srcAddrIdx] === '-' || parts[dstAddrIdx] === '-') return null;
  
  const logStatus = parts[statusIdx] as 'OK' | 'NODATA' | 'SKIPDATA';
  if (logStatus !== 'OK') return null;
  
  const action = parts[actionIdx] as 'ACCEPT' | 'REJECT';
  if (action !== 'ACCEPT' && action !== 'REJECT') return null;
  
  try {
    return {
      timestamp,
      eniId: parts[eniIdx],
      version: parseInt(parts[versionIdx]) || 2,
      accountId: parts[accountIdx],
      srcAddr: parts[srcAddrIdx],
      dstAddr: parts[dstAddrIdx],
      srcPort: parseInt(parts[srcPortIdx]) || 0,
      dstPort: parseInt(parts[dstPortIdx]) || 0,
      protocol: parseInt(parts[protocolIdx]) || 0,
      packets: parseInt(parts[packetsIdx]) || 0,
      bytes: parseInt(parts[bytesIdx]) || 0,
      startTime: parseInt(parts[startIdx]) || 0,
      endTime: parseInt(parts[endIdx]) || 0,
      action,
      logStatus
    };
  } catch {
    return null;
  }
}

// Determine traffic direction
function determineDirection(
  srcAddr: string, 
  dstAddr: string, 
  vpcCidrs: string[]
): 'inbound' | 'outbound' | 'unknown' {
  const srcInVpc = vpcCidrs.some(cidr => isIPInCIDR(srcAddr, cidr));
  const dstInVpc = vpcCidrs.some(cidr => isIPInCIDR(dstAddr, cidr));
  
  if (srcInVpc && !dstInVpc) return 'outbound';
  if (!srcInVpc && dstInVpc) return 'inbound';
  if (srcInVpc && dstInVpc) return 'inbound'; // Internal traffic, treat as inbound to dest
  
  // Fallback: use private IP heuristic
  const srcPrivate = isPrivateIP(srcAddr);
  const dstPrivate = isPrivateIP(dstAddr);
  
  if (srcPrivate && !dstPrivate) return 'outbound';
  if (!srcPrivate && dstPrivate) return 'inbound';
  
  return 'unknown';
}

// Chunk size for processing - yields to browser every N lines
const PARSE_CHUNK_SIZE = 2000;
const AGGREGATE_CHUNK_SIZE = 5000;

// Debug logging
const DEBUG = true;
const log = (...args: unknown[]) => {
  if (DEBUG) {
    console.log('[FlowParser]', new Date().toISOString(), ...args);
  }
};

/**
 * Hook to parse VPC Flow Logs using chunked processing.
 * This prevents the UI from freezing during large file processing
 * by yielding to the browser's event loop periodically.
 */
/**
 * Pre-process input: detect JSON format and extract flow log messages
 */
function preprocessInput(text: string): string[] {
  const trimmed = text.trim();
  
  // Check if it looks like JSON
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const json = JSON.parse(trimmed);
      log('Detected JSON input');
      
      // Format 1: CloudWatch Logs Insights / AWS Console export
      // { "events": [ { "message": "..." }, ... ] }
      if (json.events && Array.isArray(json.events)) {
        log('Format: CloudWatch events array with', json.events.length, 'events');
        return json.events
          .map((e: { message?: string }) => e.message || '')
          .filter((m: string) => m && !m.includes('NODATA') && !m.includes('SKIPDATA'));
      }
      
      // Format 2: Array of events directly
      // [ { "message": "..." }, ... ]
      if (Array.isArray(json)) {
        log('Format: Direct events array with', json.length, 'items');
        return json
          .map((e: { message?: string }) => e.message || '')
          .filter((m: string) => m && !m.includes('NODATA') && !m.includes('SKIPDATA'));
      }
      
      // Format 3: Single record with "records" array
      // { "records": [ { ... flow log fields ... } ] }
      if (json.records && Array.isArray(json.records)) {
        log('Format: Records array with', json.records.length, 'records');
        // Convert structured records to flow log format string
        interface FlowLogJsonRecord {
          message?: string;
          accountId?: string;
          interfaceId?: string;
          eniId?: string;
          srcAddr?: string;
          dstAddr?: string;
          srcPort?: string | number;
          dstPort?: string | number;
          protocol?: string | number;
          packets?: string | number;
          bytes?: string | number;
          start?: string | number;
          startTime?: string | number;
          end?: string | number;
          endTime?: string | number;
          action?: string;
          logStatus?: string;
        }
        return json.records.map((r: FlowLogJsonRecord) => {
          if (r.message) return r.message;
          // Build message from fields
          return `2 ${r.accountId || '-'} ${r.interfaceId || r.eniId || '-'} ${r.srcAddr || '-'} ${r.dstAddr || '-'} ${r.srcPort || '-'} ${r.dstPort || '-'} ${r.protocol || '-'} ${r.packets || '-'} ${r.bytes || '-'} ${r.start || r.startTime || '-'} ${r.end || r.endTime || '-'} ${r.action || '-'} ${r.logStatus || 'OK'}`;
        }).filter((m: string) => m && !m.includes('NODATA'));
      }
      
      log('Unknown JSON format, treating as text');
    } catch (e) {
      log('JSON parse failed, treating as plain text');
    }
  }
  
  // Plain text: split by newlines
  return trimmed.split('\n');
}

export function useFlowParser(): UseFlowParserReturn {
  const [streams, setStreams] = useState<FlowStream[]>([]);
  const [progress, setProgress] = useState<ParseProgress>({ 
    progress: 0, 
    phase: 'idle' 
  });
  const [stats, setStats] = useState<ParseStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const cancelledRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const parseFlowLogs = useCallback((text: string, vpcCidrs: string[] = []) => {
    log('parseFlowLogs called, text length:', text.length, 'vpcCidrs:', vpcCidrs);
    
    // Cancel any existing processing
    cancelledRef.current = true;
    clearPendingTimeout();
    
    // Reset state after a microtask to ensure cancellation is processed
    setTimeout(() => {
      cancelledRef.current = false;
      setStreams([]);
      setProgress({ progress: 0, phase: 'parsing' });
      setStats(null);
      setError(null);
      setIsProcessing(true);
      
      log('Starting to preprocess input...');
      const splitStart = performance.now();
      // Preprocess input (handles JSON and plain text)
      const lines = preprocessInput(text);
      const totalLines = lines.length;
      log('Split complete in', (performance.now() - splitStart).toFixed(2), 'ms. Total lines:', totalLines);
      const records: VPCFlowRecord[] = [];
      let skippedCount = 0;
      let currentIndex = 0;
      
      // Phase 1: Parse lines in chunks
      const parseChunk = () => {
        const chunkStart = performance.now();
        log('parseChunk starting at index:', currentIndex, '/', totalLines);
        
        if (cancelledRef.current) {
          log('Cancelled, stopping');
          setIsProcessing(false);
          return;
        }
        
        const endIndex = Math.min(currentIndex + PARSE_CHUNK_SIZE, totalLines);
        
        for (let i = currentIndex; i < endIndex; i++) {
          const line = lines[i];
          if (!line.trim()) {
            skippedCount++;
            continue;
          }
          
          const record = parseLine(line);
          if (record) {
            records.push(record);
          } else {
            skippedCount++;
          }
        }
        
        currentIndex = endIndex;
        const parseProgress = (currentIndex / totalLines) * 50; // First 50%
        
        log('parseChunk done in', (performance.now() - chunkStart).toFixed(2), 'ms. Progress:', parseProgress.toFixed(1) + '%', 'Records so far:', records.length);
        
        setProgress({
          progress: parseProgress,
          phase: 'parsing',
          currentLine: currentIndex,
          totalLines
        });
        
        if (currentIndex < totalLines) {
          // Yield to browser, then continue
          log('Scheduling next parse chunk...');
          timeoutRef.current = setTimeout(parseChunk, 0);
        } else {
          // Parsing done, start aggregation
          log('Parsing complete. Total valid records:', records.length, 'Skipped:', skippedCount);
          aggregateRecords(records, skippedCount, vpcCidrs, totalLines);
        }
      };
      
      // Phase 2: Aggregate records into streams
      const aggregateRecords = (
        allRecords: VPCFlowRecord[], 
        skipped: number, 
        cidrs: string[],
        totalLinesCount: number
      ) => {
        log('Starting aggregation phase. Total records to aggregate:', allRecords.length);
        setProgress({ progress: 50, phase: 'aggregating' });
        
        const streamMap = new Map<string, FlowStream>();
        let aggIndex = 0;
        const totalRecords = allRecords.length;
        
        const aggregateChunk = () => {
          const chunkStart = performance.now();
          log('aggregateChunk starting at index:', aggIndex, '/', totalRecords);
          
          if (cancelledRef.current) {
            log('Cancelled during aggregation');
            setIsProcessing(false);
            return;
          }
          
          const endIndex = Math.min(aggIndex + AGGREGATE_CHUNK_SIZE, totalRecords);
          
          for (let i = aggIndex; i < endIndex; i++) {
            const record = allRecords[i];
            
            // Create unique stream key
            const key = `${record.eniId}|${record.srcAddr}|${record.dstAddr}|${record.srcPort}|${record.dstPort}|${record.protocol}|${record.action}`;
            
            if (!streamMap.has(key)) {
              const direction = determineDirection(record.srcAddr, record.dstAddr, cidrs);
              
              streamMap.set(key, {
                id: key,
                eniId: record.eniId,
                srcAddr: record.srcAddr,
                dstAddr: record.dstAddr,
                srcPort: record.srcPort,
                dstPort: record.dstPort,
                protocol: record.protocol,
                protocolName: getProtocolName(record.protocol),
                direction,
                action: record.action,
                totalPackets: 0,
                totalBytes: 0,
                flowCount: 0,
                firstSeen: record.timestamp,
                lastSeen: record.timestamp,
                records: []
              });
            }
            
            const stream = streamMap.get(key)!;
            stream.totalPackets += record.packets;
            stream.totalBytes += record.bytes;
            stream.flowCount++;
            
            if (record.timestamp < stream.firstSeen) {
              stream.firstSeen = record.timestamp;
            }
            if (record.timestamp > stream.lastSeen) {
              stream.lastSeen = record.timestamp;
            }
          }
          
          aggIndex = endIndex;
          const aggProgress = 50 + ((aggIndex / totalRecords) * 50); // Last 50%
          
          log('aggregateChunk done in', (performance.now() - chunkStart).toFixed(2), 'ms. Progress:', aggProgress.toFixed(1) + '%', 'Unique streams:', streamMap.size);
          
          setProgress({
            progress: aggProgress,
            phase: 'aggregating'
          });
          
          if (aggIndex < totalRecords) {
            // Yield to browser, then continue
            log('Scheduling next aggregate chunk...');
            timeoutRef.current = setTimeout(aggregateChunk, 0);
          } else {
            // Aggregation complete
            log('Aggregation complete. Starting finishProcessing...');
            finishProcessing(streamMap, allRecords.length, skipped, totalLinesCount);
          }
        };
        
        // Start aggregation
        log('Scheduling first aggregate chunk...');
        timeoutRef.current = setTimeout(aggregateChunk, 0);
      };
      
      // Detect stateful connections by finding reverse flows
      const detectStatefulConnections = (streams: FlowStream[]): void => {
        log('Detecting stateful connections...');
        const start = performance.now();
        
        // Create a map for quick lookup of reverse flows
        // Key: "srcIP|dstIP|srcPort|dstPort|protocol" 
        const flowLookup = new Map<string, FlowStream>();
        
        for (const stream of streams) {
          // Create key for this flow
          const key = `${stream.srcAddr}|${stream.dstAddr}|${stream.srcPort}|${stream.dstPort}|${stream.protocol}`;
          flowLookup.set(key, stream);
        }
        
        let initiatorCount = 0;
        let responseCount = 0;
        
        for (const stream of streams) {
          // Look for reverse flow (swap src/dst)
          const reverseKey = `${stream.dstAddr}|${stream.srcAddr}|${stream.dstPort}|${stream.srcPort}|${stream.protocol}`;
          const reverseFlow = flowLookup.get(reverseKey);
          
          if (reverseFlow) {
            // Found a related flow! Determine who is initiator vs response
            const srcPortIsWellKnown = WELL_KNOWN_PORTS.has(stream.srcPort);
            const dstPortIsWellKnown = WELL_KNOWN_PORTS.has(stream.dstPort);
            
            // Link the flows
            stream.relatedStreamId = reverseFlow.id;
            
            if (dstPortIsWellKnown && !srcPortIsWellKnown) {
              // This flow is going TO a well-known port -> INITIATOR
              stream.connectionType = 'initiator';
              stream.serverPort = stream.dstPort;
              initiatorCount++;
            } else if (srcPortIsWellKnown && !dstPortIsWellKnown) {
              // This flow is coming FROM a well-known port -> RESPONSE
              stream.connectionType = 'response';
              stream.serverPort = stream.srcPort;
              stream.isStatefulAllowed = true; // Allowed by stateful SG behavior
              responseCount++;
            } else {
              // Both or neither are well-known ports, use timestamp to determine
              if (stream.firstSeen <= reverseFlow.firstSeen) {
                stream.connectionType = 'initiator';
                initiatorCount++;
              } else {
                stream.connectionType = 'response';
                stream.isStatefulAllowed = true;
                responseCount++;
              }
              // Use the lower port as server port
              stream.serverPort = Math.min(stream.srcPort, stream.dstPort);
            }
          } else {
            // No reverse flow found - could be one-way traffic or incomplete capture
            stream.connectionType = 'unknown';
            // Still try to identify server port
            if (WELL_KNOWN_PORTS.has(stream.dstPort)) {
              stream.serverPort = stream.dstPort;
            } else if (WELL_KNOWN_PORTS.has(stream.srcPort)) {
              stream.serverPort = stream.srcPort;
            }
          }
        }
        
        log('Stateful detection complete in', (performance.now() - start).toFixed(2), 'ms.');
        log('Initiators:', initiatorCount, 'Responses:', responseCount);
      };
      
      // Finish and report results
      const finishProcessing = (
        streamMap: Map<string, FlowStream>,
        validCount: number,
        skipped: number,
        totalLinesCount: number
      ) => {
        log('finishProcessing called. Stream count:', streamMap.size);
        const finishStart = performance.now();
        
        if (cancelledRef.current) {
          log('Cancelled during finish');
          setIsProcessing(false);
          return;
        }
        
        log('Converting map to array...');
        const resultStreams = Array.from(streamMap.values());
        log('Converted in', (performance.now() - finishStart).toFixed(2), 'ms');
        
        // Detect stateful connections
        detectStatefulConnections(resultStreams);
        
        // Sort by total bytes (most traffic first)
        log('Sorting', resultStreams.length, 'streams...');
        const sortStart = performance.now();
        resultStreams.sort((a, b) => b.totalBytes - a.totalBytes);
        log('Sorted in', (performance.now() - sortStart).toFixed(2), 'ms');
        
        log('Calculating stats...');
        const statsStart = performance.now();
        const finalStats: ParseStats = {
          totalRecords: totalLinesCount,
          validRecords: validCount,
          skippedRecords: skipped,
          uniqueStreams: resultStreams.length,
          acceptCount: resultStreams.filter(s => s.action === 'ACCEPT').length,
          rejectCount: resultStreams.filter(s => s.action === 'REJECT').length
        };
        log('Stats calculated in', (performance.now() - statsStart).toFixed(2), 'ms');
        
        log('Setting state with', resultStreams.length, 'streams...');
        const stateStart = performance.now();
        setStreams(resultStreams);
        setStats(finalStats);
        setProgress({ progress: 100, phase: 'complete' });
        setIsProcessing(false);
        log('State set in', (performance.now() - stateStart).toFixed(2), 'ms');
        log('COMPLETE! Total finish time:', (performance.now() - finishStart).toFixed(2), 'ms');
      };
      
      // Start parsing
      timeoutRef.current = setTimeout(parseChunk, 0);
    }, 0);
  }, [clearPendingTimeout]);
  
  const cancelParsing = useCallback(() => {
    cancelledRef.current = true;
    clearPendingTimeout();
    setProgress({ progress: 0, phase: 'idle' });
    setIsProcessing(false);
  }, [clearPendingTimeout]);
  
  return {
    parseFlowLogs,
    cancelParsing,
    streams,
    progress,
    stats,
    error,
    isProcessing
  };
}
